import React, { useEffect, useState, useRef } from 'react';
import dayjs from 'dayjs';
import { useDispatch, useSelector } from 'react-redux';
import { clientActions } from '../../../../../Clients/store';
import { getClientsShort } from '../../../../../Clients/store/selectors';
import {
  getCalendarWorkers,
  getCalendarWorkerServices,
  getTaskById,
} from '../../../../store/selectors';
import InputSelector from '../../../../../Common/CustomSelector/InputSelector';
import MultiplySelector from '../../../../../Common/CustomSelector/MultiplySelector';
import TextField from '../../../../../Common/TextField';
import { ReactComponent as Calendar } from '../../../../../../assets/svg/icons/calendar_blue.svg';
import { ReactComponent as Scissors } from '../../../../../../assets/svg/icons/scissors_blue.svg';
import { ReactComponent as Person } from '../../../../../../assets/svg/icons/person_blue.svg';
import { ReactComponent as Armchair } from '../../../../../../assets/svg/icons/armchair_blue.svg';
import { ReactComponent as Bin } from '../../../../../../assets/svg/icons/trash_red.svg';
import { ReactComponent as PlusIcon } from '../../../../../../assets/svg/icons/aggiungi_blue.svg';
import { ReactComponent as ClockIcon } from '../../../../../../assets/svg/icons/orari_blue.svg';
import { Field, Form, Formik } from 'formik';
import * as yup from 'yup';
import styles from './TaskModal.module.scss';
import Switcher from '../../../../../Common/Switcher';
import HoveredInfoIcon from '../../../../../Common/HoveredInfoIcon';
import { Button } from '@mui/material';
import clx from 'classnames';
import CustomDatePicker from '../../../../../Common/CustomDatePicker';
import CustomTimePicker from '../../../../../Common/CustomTimePicker';
import { roundToNearestFive } from '../../../../../Common/TimeStepper';
import { schedulerActions } from '../../../../store';
import ConfirmDeleteModal from '../../../../../Common/ConfirmDeleteModal';
import useWindowSize from '../../../../../../utils/useWindowSize';
import ConfirmSecondaryModal from '../../../../../Common/ConfirmSecondaryModal';
import constants from 'shared/Loader/store/constants';
import { getSpecifLoader } from 'shared/Loader/store/selectors';
import Spinner from 'Containers/Common/Spinner';
import { useTranslation } from 'react-i18next';
import RecurrenceFrequencySelect from '../RecurrenceFrequencySelect';

const composeBookingNote = (values) => {
  const note = String(values.description || '').trim();
  const walkIn = !values.client_id ? String(values.clientName || '').trim() : '';
  if (walkIn && note && !note.includes(walkIn)) return `${walkIn}\n${note}`;
  return note || walkIn;
};

const FALLBACK_RECURRENCE = {
  1: 'Ogni settimana',
  2: 'Ogni due settimane',
  3: 'Ogni tre settimane',
  4: 'Ogni quattro settimane',
};

const TaskModal = ({
  taskValue: { day, workerId, task },
  closeModal,
  isEditForm = false,
  isMobile,
  isTablet = false,
  clients,
}) => {
  const dispatch = useDispatch();
  const [confirmationModalIsOpen, setConfirmationModalIsOpen] = useState(false);
  const loading = useSelector(getSpecifLoader(constants.CLIENT));
  const [isLoading, setIsLoading] = useState(false);
  const { t } = useTranslation();
  // Su tablet la nota resta un campo proprio: il nome scritto non deve passare dalla lista clienti.
  const showNote = Boolean(isTablet) || !isMobile;

  const recurrenceOptions = [
    {
      name: t('scheduler.taskModal.recurrence.weekly', FALLBACK_RECURRENCE[1]),
      value: 1,
    },
    {
      name: t('scheduler.taskModal.recurrence.biweekly', FALLBACK_RECURRENCE[2]),
      value: 2,
    },
    {
      name: t('scheduler.taskModal.recurrence.triweekly', FALLBACK_RECURRENCE[3]),
      value: 3,
    },
    {
      name: t('scheduler.taskModal.recurrence.quadriweekly', FALLBACK_RECURRENCE[4]),
      value: 4,
    },
  ];

  const clientsRequested = useRef(false);
  useEffect(() => {
    if ((clients || []).length > 0 || clientsRequested.current) return;
    clientsRequested.current = true;
    dispatch(
      clientActions.GET_CLIENTS.REQUEST({}, () => {}, {
        loader: constants.CLIENT,
      }),
    );
  }, [dispatch, clients]);

  const workers = useSelector(getCalendarWorkers());
  const services = useSelector(getCalendarWorkerServices());
  const taskToEdit = isEditForm ? useSelector(getTaskById(task.idprenotazione)) : {};
  const initialStart = task?.unixStartTime
    ? dayjs(task.unixStartTime * 1000)
    : roundToNearestFive(dayjs());
  const initialEnd = task?.unixEndTime
    ? dayjs(task.unixEndTime * 1000)
    : initialStart.add(30, 'minutes');
  const initialValues = {
    worker_id: workerId || '',
    client_id: isEditForm ? taskToEdit.utente : '',
    service_ids:
      isEditForm && taskToEdit?.servizi?.length
        ? taskToEdit.servizi.map((task) => task.idServizio)
        : [],
    date: initialStart,
    startDate: initialStart,
    endDate: initialEnd,
    recurring_booking: false,
    recurrence: 1,
    recurrenceEndDate: initialStart.add(1, 'month'),
    services: services,
    description: isEditForm ? taskToEdit.nota : '',
    clientName: '',
  };
  const validationSchema = isEditForm
    ? {
        worker_id: yup.string().required(t('scheduler.taskModal.validation.selectChair')),
        startDate: yup.date().required(t('scheduler.taskModal.validation.selectTime')),
        endDate: yup.date().required(t('scheduler.taskModal.validation.selectTime')),
        date: yup.date().required(t('scheduler.taskModal.validation.dateRequired')),
      }
    : {
        worker_id: yup.string().required(t('scheduler.taskModal.validation.selectChair')),
        client_id: yup
          .number()
          .transform((value, originalValue) =>
            originalValue === '' || originalValue == null ? null : value,
          )
          .nullable()
          .when(['description', 'clientName'], ([description, clientName], schema) =>
            String(description || '').trim() || String(clientName || '').trim()
              ? schema
              : schema.required(t('scheduler.taskModal.validation.selectClientOrNote')),
          ),
        service_ids: yup
          .array()
          .min(1, t('scheduler.taskModal.validation.selectService'))
          .required(t('scheduler.taskModal.validation.selectService')),
        startDate: yup.date().required(t('scheduler.taskModal.validation.selectTime')),
        endDate: yup.date().required(t('scheduler.taskModal.validation.selectTime')),
        date: yup.date().required(t('scheduler.taskModal.validation.dateRequired')),
        description: yup.string(),
        clientName: yup.string(),
        recurring_booking: yup.boolean(),
        recurrence: yup
          .number()
          .when('recurring_booking', ([recurring_booking], schema) =>
            recurring_booking ? schema.required(t('scheduler.taskModal.validation.requiredField')) : schema.nullable(true),
          ),
        recurrenceEndDate: yup
          .date()
          .when('recurring_booking', ([recurring_booking], schema) =>
            recurring_booking ? schema.required(t('scheduler.taskModal.validation.requiredField')) : schema.nullable(true),
          ),
      };
  const removeBooking = (recurrence) => {
    dispatch(
      schedulerActions.DELETE_CALENDAR_TASK.REQUEST({
        deleteAllRicorrenti: recurrence,
        idPoltrona: taskToEdit.poltrona,
        idPrenotazione: taskToEdit.idprenotazione,
        ricorrente: taskToEdit.ricorrente,
      }),
    );
    closeModal();
  };

  const scrollToRef = useRef(null);
  const refs = {
    worker_id: scrollToRef,
    service_ids: scrollToRef,
    startDate: scrollToRef,
    client_id: scrollToRef,
  };

  const handleScroll = (ref) => {
    window.scrollTo({
      top: ref.offsetTop,
      left: 0,
      behavior: 'smooth',
    });
  };

  const loadingAfterCall = () => {
    setIsLoading(false);
    closeModal();
  };

  const stopLoading = () => setIsLoading(false);

  return (
    <>
      {isLoading && <Spinner blocking />}
      <ConfirmSecondaryModal
        isModalOpen={confirmationModalIsOpen}
        setIsModalOpen={setConfirmationModalIsOpen}
        title={t('scheduler.taskModal.cancelBooking.title')}
        subtitle={
          task?.ricorrente
            ? t('scheduler.taskModal.cancelBooking.recurringSubtitle')
            : t('scheduler.taskModal.cancelBooking.confirmSubtitle')
        }
        buttons={
          task?.ricorrente
            ? [t('scheduler.taskModal.cancelBooking.cancelThis'), t('scheduler.taskModal.cancelBooking.cancelAll')]
            : [t('scheduler.taskModal.cancelBooking.cancel')]
        }
        buttonsActions={[() => removeBooking(false), () => removeBooking(true)]}
      />
      <Formik
        initialValues={initialValues}
        validationSchema={yup.object().shape(validationSchema)}
        onSubmit={(values) => {
          if (isEditForm) {
            const formedTask = {
              idPrenotazione: taskToEdit.idprenotazione,

              orario_start: values.startDate.format('HH:mm') + ':00',
              orario_end: values.endDate.format('HH:mm') + ':00',
              data_prenotazione: values.date,
              poltrona: values.worker_id,
              nota: values.description,
            };
            dispatch(
              schedulerActions.EDIT_CALENDAR_TASK.REQUEST(formedTask, null, {
                skipLoader: true,
                errCallback: stopLoading,
              }),
            );
            closeModal();
            return;
          }

          setIsLoading(true);
          const services = values.services
            .filter(
              (service) =>
                +service.worker_id === +values.worker_id &&
                values.service_ids.includes(service.value),
            )
            .map((service) => ({ idservezio: service.value, nome: service.name }));
          const formedValues = {
            idPoltrona: values.worker_id,
            idUtente: values.client_id || null,
            servizi: services,
            oraInizio: values.startDate.format('HH:mm') + ':00',
            oraFine: values.endDate.format('HH:mm') + ':00',
            dataPrenotazione: values.date.format('YYYY-MM-DD'),
            nota: composeBookingNote(values),
            recurrence: {
              enabled: values.recurring_booking,
              to: values.recurring_booking && values.recurrenceEndDate.format('YYYY-MM-DD'),
              recurrenceType: values.recurrence,
            },
          };
          dispatch(
            schedulerActions.ADD_CALENDAR_TASK.REQUEST(
              formedValues,
              () => setTimeout(loadingAfterCall, 1000),
              { errCallback: stopLoading },
            ),
          );
        }}
      >
        {({ values, setFieldValue, errors, touched, setFieldTouched }) => {
          const servicesDuration = values.services
            .filter(
              (service) =>
                +service.worker_id === +values.worker_id &&
                values.service_ids.includes(service.value),
            )
            .reduce((acc, item) => acc + (item.duration || 0), 0);
          const durationMinutes = Math.max(
            5,
            values.endDate && values.startDate
              ? values.endDate.diff(values.startDate, 'minute')
              : 30,
          );

          const setStartKeepingDuration = (nextStart) => {
            const kept = servicesDuration || durationMinutes || 30;
            setFieldValue('startDate', nextStart);
            setFieldTouched('startDate', false);
            setFieldValue('endDate', nextStart.add(kept, 'minutes'));
            setFieldTouched('endDate', false);
          };

          const setBookingDate = (date) => {
            if (!date) return;
            const nextStart = values.startDate
              .year(date.year())
              .month(date.month())
              .date(date.date());
            setFieldValue('date', date);
            setStartKeepingDuration(nextStart);
            setFieldValue('recurrenceEndDate', date.add(1, 'month'));
            setFieldTouched('date', false);
            setFieldTouched('recurrenceEndDate', false);
          };

          return (
            <Form
              className={clx(styles.create_task_form, {
                [styles.create_task_form_mobile]: isMobile,
              })}
            >
              <div className={styles.body} data-modal-scroll>
                <div className={styles.inputs_wrapper}>
                  <div className={styles.input_wrapper}>
                    <InputSelector
                      options={workers}
                      value={values.worker_id}
                      setValue={(id) => {
                        setFieldValue('worker_id', id);
                        setFieldTouched('worker_id', false);
                        if (id !== values.worker_id && !isEditForm) {
                          setFieldValue('service_ids', []);
                          setFieldTouched('service_ids', false);
                        }
                      }}
                      placeholder={t('scheduler.taskModal.selectChair')}
                      title={t('scheduler.taskModal.chair')}
                      icon={<Armchair />}
                      withIcon={true}
                      error={touched.worker_id && errors.worker_id}
                      isEmptyMessage={t('scheduler.taskModal.noChairs')}
                    />
                  </div>
                  <div className={styles.input_wrapper}>
                    <MultiplySelector
                      readOnly={true}
                      options={values.services.filter(
                        (service) => +service.worker_id === +values.worker_id,
                      )}
                      value={values.service_ids}
                      closeOnClick={true}
                      setValue={(ids) => {
                        const activeServicesDuration = values.services
                          .filter(
                            (service) =>
                              +service.worker_id === +values.worker_id && ids.includes(service.value),
                          )
                          .reduce((acc, item) => acc + (item.duration || 0), 0);
                        // Un solo update Formik → meno re-render al tap
                        setFieldValue('service_ids', ids, false);
                        setFieldValue(
                          'endDate',
                          values.startDate.add(activeServicesDuration || 30, 'minutes'),
                          false,
                        );
                      }}
                      placeholder={t('scheduler.taskModal.selectService')}
                      title={t('scheduler.taskModal.service')}
                      icon={values.service_ids.length == 0 ? <Scissors /> : <PlusIcon />}
                      withIcon={true}
                      disabled={isEditForm}
                      error={touched.service_ids && errors.service_ids}
                      isEmptyMessage={t('scheduler.taskModal.noServices')}
                    />
                  </div>
                </div>

                <div className={styles.when_section}>
                  <CustomDatePicker
                    value={values.date}
                    setValue={setBookingDate}
                    placeholder={t('scheduler.taskModal.selectDate')}
                    title={t('scheduler.taskModal.date')}
                    icon={<Calendar />}
                    withIcon={true}
                    error={touched.date && errors.date}
                  />
                  <div className={styles.time_row}>
                    <CustomTimePicker
                      value={values.startDate}
                      setValue={setStartKeepingDuration}
                      placeholder={t('scheduler.taskModal.startTime')}
                      title={t('scheduler.taskModal.startTime')}
                      icon={<ClockIcon />}
                      withIcon={true}
                      timeStep={5}
                      error={touched.startDate && errors.startDate}
                    />
                    <CustomTimePicker
                      value={values.endDate}
                      setValue={(date) => {
                        setFieldValue('endDate', date);
                        setFieldTouched('endDate', false);
                      }}
                      placeholder={t('scheduler.taskModal.endTime')}
                      title={t('scheduler.taskModal.endTime')}
                      icon={<ClockIcon />}
                      withIcon={true}
                      timeStep={5}
                      minTime={values.startDate.add(5, 'minutes')}
                      error={touched.endDate && errors.endDate}
                    />
                  </div>
                  <p className={styles.time_step_hint}>
                    {t('scheduler.taskModal.timeStepHint')}
                  </p>
                </div>

                <div className={styles.inputs_wrapper}>
                  <div className={styles.input_wrapper}>
                    <InputSelector
                      loading={loading}
                      options={clients}
                      newValue={showNote ? values.clientName : values.description}
                      acceptNewValue={isMobile}
                      clearNewValueOnSelect={!showNote}
                      openOnFocus={!isTablet}
                      value={values.client_id}
                      setNewValue={(value) => {
                        setFieldValue(showNote ? 'clientName' : 'description', value);
                        setFieldTouched('client_id', false);
                      }}
                      setValue={(id) => {
                        setFieldValue('client_id', id);
                        setFieldTouched('client_id', false);
                      }}
                      placeholder={t('scheduler.taskModal.selectClient')}
                      title={showNote ? t('scheduler.taskModal.client') : t('scheduler.taskModal.clientOrNote')}
                      icon={<Person />}
                      withIcon={true}
                      disabled={isEditForm}
                      error={touched.client_id && errors.client_id}
                      isEmptyMessage={t('scheduler.taskModal.noClients')}
                      number={true}
                    />
                  </div>
                </div>

                <div className={styles.field_block}>
                  {showNote && (
                    <Field
                      name='description'
                      id='description'
                      label={t('scheduler.taskModal.note')}
                      placeholder={t('scheduler.taskModal.notePlaceholder')}
                      component={TextField}
                      type='text'
                      textarea={true}
                      height={'88px'}
                      width={'100%'}
                      margin={'0'}
                    />
                  )}
                  {!isEditForm && (
                    <div className={styles.recurrence_panel}>
                      <div className={styles.switcher_wrapper}>
                        <HoveredInfoIcon
                          iconPosition='right'
                          description={t('scheduler.taskModal.recurringInfo')}
                          title={t('scheduler.taskModal.recurringBooking')}
                        />
                        <div className={styles.switcher_container}>
                          <Switcher
                            value={!values.recurring_booking}
                            setValue={(bool) => setFieldValue('recurring_booking', bool)}
                            isFullWidth={false}
                          />
                        </div>
                      </div>

                      {values.recurring_booking ? (
                        <div className={styles.recurrence_fields}>
                          <div className={styles.time_input_wrapper}>
                            <CustomDatePicker
                              value={values.recurrenceEndDate}
                              setValue={(date) => {
                                setFieldValue('recurrenceEndDate', date);
                                setFieldTouched('recurrenceEndDate', false);
                              }}
                              placeholder={t('scheduler.taskModal.selectDate')}
                              title={t('scheduler.taskModal.until')}
                              icon={<Calendar />}
                              withIcon={true}
                              minDate={values.date}
                              maxDate={dayjs(values.date).add(1, 'year').toDate()}
                              error={touched.recurrenceEndDate && errors.recurrenceEndDate}
                            />
                          </div>
                          <RecurrenceFrequencySelect
                            label={t('scheduler.taskModal.recurrence.label')}
                            value={values.recurrence ?? 1}
                            options={recurrenceOptions}
                            onChange={(next) => {
                              setFieldValue('recurrence', next);
                              setFieldTouched('recurrence', false);
                            }}
                          />
                        </div>
                      ) : null}
                    </div>
                  )}
                </div>
              </div>
              <div
                className={clx(styles.form_actions, {
                  [styles.edit_actions]: isEditForm,
                })}
              >
                <Button
                  onClick={() => setConfirmationModalIsOpen(true)}
                  className={styles.delete_button}
                >
                  <Bin />
                    {t('common.actions.cancel')}
                </Button>
                <div className={styles.confirm_button_block}>
                  {!isMobile && (
                    <Button onClick={closeModal} className={styles.return_button}>
                      {t('common.actions.cancel')}
                    </Button>
                  )}
                  <Button className={styles.confirm_button} variant='contained' type={'submit'}>
                    {t('common.actions.confirm')}
                  </Button>
                </div>
              </div>
            </Form>
          );
        }}
      </Formik>
    </>
  );
};

export default TaskModal;
